import { type FormEvent, type ReactNode, useState } from 'react';
import { ErrorBanner } from './ErrorBanner';

export function AddModal({
  title,
  buttonLabel,
  submitLabel = 'Save',
  subtitle,
  children,
  onSubmit,
  showSubmit = true,
  disabled = false,
  error,
  buttonClassName,
  width = 'normal',
}: {
  title: string;
  buttonLabel: string;
  submitLabel?: string;
  subtitle?: string;
  children: ReactNode;
  onSubmit?: (form: FormData) => Promise<unknown> | unknown;
  showSubmit?: boolean;
  disabled?: boolean;
  error?: unknown;
  buttonClassName?: string;
  width?: 'normal' | 'wide';
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<unknown>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving || !onSubmit) return;
    setSaving(true);
    setLocalError(null);
    try {
      await onSubmit(new FormData(event.currentTarget));
      event.currentTarget.reset();
      setOpen(false);
    } catch (nextError) {
      setLocalError(nextError);
    } finally {
      setSaving(false);
    }
  };

  return <>
    <button type="button" className={buttonClassName ?? 'add-action-button'} disabled={disabled} onClick={() => { setLocalError(null); setOpen(true); }}>
      {buttonLabel}
    </button>
    {open && <div className="modal-backdrop" role="presentation" onMouseDown={() => !saving && setOpen(false)}>
      <div className={`legacy-modal ${width === 'wide' ? 'legacy-modal-wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={`modal-${title.replace(/\s+/g, '-').toLowerCase()}`} onMouseDown={event => event.stopPropagation()}>
        <div className="legacy-modal-header">
          <div>
            <h3 id={`modal-${title.replace(/\s+/g, '-').toLowerCase()}`}>{title}</h3>
            {subtitle && <small>{subtitle}</small>}
          </div>
          <button type="button" className="modal-close" aria-label="Close" disabled={saving} onClick={() => setOpen(false)}>×</button>
        </div>
        <form className="legacy-modal-body form-grid" onSubmit={submit}>
          <ErrorBanner error={localError || error} />
          {children}
          <div className="legacy-modal-actions">
            <button type="button" className="secondary" disabled={saving} onClick={() => setOpen(false)}>{showSubmit ? 'Cancel' : 'Close'}</button>
            {showSubmit && <button type="submit" disabled={saving || !onSubmit}>{saving ? 'Saving...' : submitLabel}</button>}
          </div>
        </form>
      </div>
    </div>}
  </>;
}
